import { useNavigate } from "react-router";
import { RegularDealsStoreProvider, useRegularDealsStore } from "~/lib/stores/regular-deals";
import { DealCard } from "./DealCard";
import { DealCardSkeleton } from "~/components/skeleton/DealCardSkeleton";
import { buildShopChannel } from "~/lib/deal-channel";
import { useFetchOnce } from "~/lib/hooks/useFetchOnce";
import { useUserStore } from "~/lib/stores/user";
import { AnalyticsEventBuilder } from "~/lib/analytics";
import { saveAnalyticsContext } from "~/lib/analytics-context";
import type { DealClientListData } from "~/types/deal";
import { ChevronRight } from "lucide-react";
import { Button } from "~/components/ui/button";

interface ProductListSectionProps {
  categoryId: string;
  title: string;
  tabId: string;
  deals?: DealClientListData[]; // Optional: pre-fetched deals
}

function ProductListSectionContent({ categoryId, title, tabId, deals: preloadedDeals }: ProductListSectionProps) {
  const navigate = useNavigate();
  const { deals: fetchedDeals, isLoading, fetchDeals } = useRegularDealsStore();
  const user = useUserStore(state => state.user);

  const channelString = JSON.stringify(buildShopChannel(tabId));

  // Only fetch if deals are not provided and not already fetched
  useFetchOnce(
    !preloadedDeals && fetchedDeals.length === 0 ? `${categoryId}|${tabId}` : null,
    () => {
      const channel = buildShopChannel(tabId);
      fetchDeals(categoryId, undefined, channel);
    },
    !isLoading
  );

  // Use preloaded deals if available, otherwise use fetched deals
  // Limit to first 5 items for display
  const allDeals = preloadedDeals || fetchedDeals;
  const deals = allDeals.slice(0, 5);

  const handleViewAllClick = () => {
    navigate(`/shop/category?categoryId=${encodeURIComponent(categoryId)}`);
  };

  if (isLoading && allDeals.length === 0) {
    return (
      <section className="flex flex-col gap-[16px]">
        <div className="relative">
          <h2 className="text-[30px] leading-[34px] font-bold tracking-[-0.3px]">{title}</h2>
        </div>
        <div className="flex gap-[24px]">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="max-w-[200.6px] min-w-0 flex-1">
              <DealCardSkeleton />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (deals.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-[16px]">
      <div className="relative">
        <h2 className="text-[30px] leading-[34px] font-bold tracking-[-0.3px]">{title}</h2>
        <Button
          onClick={handleViewAllClick}
          className="absolute top-1/2 right-0 size-[40px] -translate-y-1/2 rounded-[14px] border border-black/10 bg-[rgba(250,250,250,0.9)] p-[12px] backdrop-blur hover:bg-[rgba(250,250,250,1)]"
          variant="ghost"
          size="icon"
        >
          <ChevronRight className="size-[16px] text-neutral-900" />
        </Button>
      </div>
      <div className="flex gap-[24px] overflow-clip">
        {deals.map((deal, index) => (
          <div key={deal._id} className="max-w-[200.6px] min-w-0 flex-1">
            <DealCard
              deal={deal}
              channel={channelString}
              position={index}
              onClick={async () => {
                if (user) {
                  const contextAttrs = AnalyticsEventBuilder.createContextAttrs(channelString, index);
                  const itemAttrs = AnalyticsEventBuilder.createItemAttrs({
                    currency: user.currency || "USD",
                    price_dash: deal.price || 0,
                  });
                  await saveAnalyticsContext(deal._id, deal._productId, deal.reqId || "", contextAttrs, itemAttrs);
                }

                let url = `/deal/detail?deal_id=${deal._id}`;
                if (deal.reqId) {
                  url += `&req_id=${deal.reqId}`;
                }
                window.open(url, "_blank");
              }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function ProductListSection(props: ProductListSectionProps) {
  return (
    <RegularDealsStoreProvider channelId={props.categoryId}>
      <ProductListSectionContent {...props} />
    </RegularDealsStoreProvider>
  );
}
